import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import * as Yup from "yup";
import axiosInstance from "../../../utils/axiosInstance";
import { modalStyle } from "../../../utils/styles";

export default function RoleModal({ open, onClose, onSubmit, role }) {
  const [permissions, setPermissions] = useState([]);

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/permissions`,
      );

      if (response.status === 200) {
        setPermissions(response.data);
      }
    } catch (error) {}
  }, []);

  useEffect(() => {
    if (open) {
      fetchPermissions();
    }
  }, [open, fetchPermissions]);

  const formik = useFormik({
    initialValues: {
      name: role ? role.name : "",
      permission: role?.permissions ? role.permissions.map((p) => p.id) : [],
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Role name is required"),
      permission: Yup.array().of(Yup.number()), // Array of integers for permission IDs
    }),
    onSubmit: (values) => {
      onSubmit(values, role?.id);

      formik.resetForm();
      onClose();
    },
  });

  const handlePermissionChange = (event) => {
    const selectedPermissions = formik.values.permission;
    const value = parseInt(event.target.value);

    if (event.target.checked) {
      // Add permission if checked
      formik.setFieldValue("permission", [...selectedPermissions, value]);
    } else {
      // Remove permission if unchecked
      formik.setFieldValue(
        "permission",
        selectedPermissions.filter((id) => id !== value),
      );
    }
  };

  return (
    <div>
      <Modal open={open} onClose={onClose}>
        <Box sx={modalStyle}>
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography id="modal-modal-title" variant="h6" component="h2">
            {role ? "Update Role" : "Add New Role"}
          </Typography>

          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              placeholder="Role Name"
              variant="outlined"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />

            {/* Permission Checkboxes */}
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Permissions
            </Typography>
            <Grid
              container
              spacing={2}
              sx={{
                maxHeight: 300,
                overflowY: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                p: 2,
              }}
            >
              {permissions && permissions.length > 0 ? (
                permissions.map((permission) => (
                  <Grid item xs={6} key={permission.id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formik.values.permission.includes(
                            permission.id,
                          )}
                          onChange={handlePermissionChange}
                          value={permission.id}
                        />
                      }
                      label={permission.name}
                    />
                  </Grid>
                ))
              ) : (
                <Typography>Please add some permissions first</Typography>
              )}
            </Grid>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              sx={{ mt: 3 }}
            >
              {role ? "Update Role" : "Add Role"}
            </Button>
          </form>
        </Box>
      </Modal>
    </div>
  );
}
